import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Keyboard, Autoplay, Grid } from 'swiper/modules';

class CarouselSwiper extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    // Get settings from script tag
    const settingsScript = this.querySelector('script[type="application/json"]');
    const settings = settingsScript ? JSON.parse(settingsScript.textContent) : {};
    
    // Default settings
    const defaultSettings = {
      slidesPerView: 'auto',
      spaceBetween: 20,
      centeredSlides: false,
      grabCursor: true,
      freeMode: false,
      progressBarSelector: null,
      navigation: false,
      breakpoints: {},
      autoplay: false,
      loop: false,
      loopAdditionalSlides: 0,
      speed: 300
    };

    const config = { ...defaultSettings, ...settings };


    let autoplayConfig = false;
    if (config.autoplay) {
      const autoplayDefaults = {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      };
      autoplayConfig = typeof config.autoplay === 'object'
        ? { ...autoplayDefaults, ...config.autoplay }
        : autoplayDefaults;
    }

    const addOffsetRight = config?.addOffsetRight || false;
    if (typeof addOffsetRight === 'number' && addOffsetRight > 0) {
      config.addOffsetRight = addOffsetRight;
    } else if (addOffsetRight) {
      config.addOffsetRight = 24;
    } else {
      config.addOffsetRight = 0;
    }


    const addOffsetLeft = config?.addOffsetLeft || false;
    if(addOffsetRight) {
      config.addOffsetLeft = 24
    }
    else {
      config.addOffsetLeft = 0;
    }
    

    const parent = this.closest('[data-swiper-parent]') || this;
    // Find the swiper container
    const swiperContainer = this.querySelector('.swiper');
    
    if (swiperContainer) {
      // Find navigation buttons if navigation is enabled
      const navigationConfig = {};
      if (config.navigation) {
        // Try to find existing navigation buttons first
        let prevButton = parent ? parent.querySelector('.swiper-button-prev') : this.querySelector('.swiper-button-prev');
        let nextButton = parent ? parent.querySelector('.swiper-button-next') : this.querySelector('.swiper-button-next');
        
        if (!prevButton && !nextButton) {
          prevButton = parent ? parent.querySelector('.carousel__nav-button--prev') : this.querySelector('.carousel__nav-button--prev');
          nextButton = parent ? parent.querySelector('.carousel__nav-button--next') : this.querySelector('.carousel__nav-button--next');
        }
        
        // If buttons don't exist, create them
        if (!prevButton && !nextButton) {
          const navigationContainer = this.querySelector('.carousel__navigation') || document.createElement('div');
          if (!navigationContainer.classList.contains('carousel__navigation')) {
            navigationContainer.className = 'carousel__navigation tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-8';
            this.appendChild(navigationContainer);
          }
          
          // Create prev button
          if (!prevButton) {
            prevButton = document.createElement('button');
            prevButton.className = 'swiper-button-prev tw-w-11 tw-h-11 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-z-10';
            prevButton.setAttribute('type', 'button');
            prevButton.setAttribute('aria-label', 'Previous slide');
            prevButton.setAttribute('tabindex', '0');
            
            prevButton.innerHTML = `
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="44" height="44" rx="22" fill="black"/>
        <path d="M19 16L25 22L19 28" stroke="white" stroke-width="2" 
        />
        </svg>
            `;
            navigationContainer.appendChild(prevButton);
          }
          
          // Create next button
          if (!nextButton) {
            nextButton = document.createElement('button');
            nextButton.className = 'swiper-button-next tw-w-11 tw-h-11 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-z-10';
            nextButton.setAttribute('type', 'button');
            nextButton.setAttribute('aria-label', 'Next slide');
            nextButton.setAttribute('tabindex', '0');
            
            nextButton.innerHTML = `
                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="44" height="44" rx="22" fill="black"/>
    <path d="M19 16L25 22L19 28" stroke="white" stroke-width="2"/>
</svg>
            `;
            navigationContainer.appendChild(nextButton);
          }
        }
        
        if (prevButton && nextButton) {
          navigationConfig.navigation = {
            prevEl: prevButton,
            nextEl: nextButton
          };
        }

      // Ensure buttons remain focusable when disabled - temporary fix for Shopify theme editor
        if (prevButton && nextButton) {
          // Override Swiper's default behavior of removing tabindex on disabled buttons
          const observer = new MutationObserver(() => {
            if (prevButton.hasAttribute('disabled')) {
              prevButton.setAttribute('tabindex', '0');
              prevButton.setAttribute('aria-disabled', 'true');
            } else {
              prevButton.removeAttribute('aria-disabled');
            }
            if (nextButton.hasAttribute('disabled')) {
              nextButton.setAttribute('tabindex', '0');
              nextButton.setAttribute('aria-disabled', 'true');
            } else {
              nextButton.removeAttribute('aria-disabled');
            }
          });
          
          observer.observe(prevButton, { attributes: true, attributeFilter: ['disabled', 'class'] });
          observer.observe(nextButton, { attributes: true, attributeFilter: ['disabled', 'class'] });
          
          // Store observer for cleanup
          this._buttonObserver = observer;
        }
      }
      else {
        if (parent.querySelector('.carousel__navigation')) {
          parent.querySelector('.carousel__navigation').style.display = 'none';
        }
      }
      
      // Only register Autoplay per instance when it's actually used.
      // Saves per-instance hook/listener setup on carousels that never autoplay.
      const modules = [Navigation, Pagination, A11y, Keyboard];
      if (autoplayConfig) {
        modules.push(Autoplay);
      }
      if (config.grid || (config.breakpoints && Object.values(config.breakpoints).some((bp) => bp && bp.grid))) {
        modules.push(Grid);
      }

      // Pagination config — accepts boolean (auto-create + bullets) or object (custom)
      let paginationConfig = false;
      if (config.pagination) {
        let paginationEl = parent.querySelector('.swiper-pagination');
        if (!paginationEl) {
          paginationEl = document.createElement('div');
          paginationEl.className = 'swiper-pagination';
          this.appendChild(paginationEl);
        }
        const paginationDefaults = {
          el: paginationEl,
          clickable: true,
          type: 'bullets'
        };
        paginationConfig = typeof config.pagination === 'object'
          ? { ...paginationDefaults, ...config.pagination, el: paginationEl }
          : paginationDefaults;
      }

      // Initialize Swiper
      this.swiper = new Swiper(swiperContainer, {
        modules,
        slidesPerView: config.slidesPerView,
        spaceBetween: config.spaceBetween,
        centeredSlides: config.centeredSlides,
        grabCursor: config.grabCursor,
        freeMode: config.freeMode,
        breakpoints: config.breakpoints,
        slidesOffsetAfter: config.addOffsetRight,
        slidesOffsetBefore: config.addOffsetLeft,
        autoplay: autoplayConfig,
        loop: config.loop,
        loopAdditionalSlides: config.loopAdditionalSlides,
        speed: config.speed,
        // Keyboard navigation
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        // Accessibility
        a11y: {
          enabled: true,
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          firstSlideMessage: 'This is the first slide',
          lastSlideMessage: 'This is the last slide',
        },
        direction: config.direction || 'horizontal',
        ...(config.grid ? { grid: config.grid } : {}),
        ...(paginationConfig ? { pagination: paginationConfig } : {}),
        ...navigationConfig,
        on: {
          slideChange: () => {
            this.updateProgress();
            this.updateButtonAccessibility();
            this.updateSlideFocusability();
          },
          init: () => {
            this.updateProgress();
            this.updateButtonAccessibility();
            this.updateSlideFocusability();
          },
          slideChangeTransitionEnd: () => {
            this.updateSlideFocusability();
          }
        }
      });
      

      
      // Update slide focusability on window resize
      let resizeTimeout;
      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.updateSlideFocusability();
        }, 100);
      };
      window.addEventListener('resize', handleResize);
      this._resizeHandler = handleResize;
    }
  }
  
  updateProgress() {
    const settingsScript = this.querySelector('script[type="application/json"]');
    const settings = settingsScript ? JSON.parse(settingsScript.textContent) : {};
    
    if (settings.progressBarSelector && this.swiper) {
      const progressBar = document.querySelector(settings.progressBarSelector);
      if (progressBar) {
        const progress = ((this.swiper.activeIndex + 1) / this.swiper.slides.length) * 100;
        progressBar.style.width = progress + '%';
      }
    }
  }
  
  updateButtonAccessibility() {
    if (!this.swiper) return;
    
    const parent = this.closest('[data-swiper-parent]') || this;
    const prevButton = parent.querySelector('.carousel__nav-button--prev, .swiper-button-prev');
    const nextButton = parent.querySelector('.carousel__nav-button--next, .swiper-button-next');
    
    if (prevButton) {
      // Ensure prev button is always focusable
      prevButton.setAttribute('tabindex', '0');
      if (this.swiper.isBeginning) {
        prevButton.setAttribute('aria-disabled', 'true');
      } else {
        prevButton.removeAttribute('aria-disabled');
      }
    }
    
    if (nextButton) {
      // Ensure next button is always focusable
      nextButton.setAttribute('tabindex', '0');
      if (this.swiper.isEnd) {
        nextButton.setAttribute('aria-disabled', 'true');
      } else {
        nextButton.removeAttribute('aria-disabled');
      }
    }
  }
  
  updateSlideFocusability() {
    if (!this.swiper) return;
    
    const swiperContainer = this.swiper.el;
    const slides = swiperContainer.querySelectorAll('.swiper-slide');
    const swiperRect = swiperContainer.getBoundingClientRect();
    
    slides.forEach((slide) => {
      const slideRect = slide.getBoundingClientRect();
      const isVisible = (
        slideRect.right > swiperRect.left &&
        slideRect.left < swiperRect.right &&
        slideRect.bottom > swiperRect.top &&
        slideRect.top < swiperRect.bottom
      );
      
      // Find all focusable elements within the slide
      const focusableElements = slide.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      
      focusableElements.forEach(element => {
        if (isVisible) {
          // Make visible slides focusable
          if (element.hasAttribute('tabindex') && element.getAttribute('tabindex') === '-1') {
            // Only remove tabindex if it was set to -1 by us
            element.removeAttribute('tabindex');
          }
          // For links and buttons, ensure they're naturally focusable
          if (element.tagName === 'A' || element.tagName === 'BUTTON') {
            element.removeAttribute('tabindex');
          }
        } else {
          // Make off-screen slides non-focusable
          element.setAttribute('tabindex', '-1');
        }
      });
    });
  }
  
  disconnectedCallback() {
    if (this._buttonObserver) {
      this._buttonObserver.disconnect();
      this._buttonObserver = null;
    }
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}
export default CarouselSwiper;

